import {
	connectToRouter,
	createBrowserNavigationUrl,
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { KnowledgeItemsPageContent, KnowledgeItemsView } from "./KnowledgeItemsPageContent";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import { tryParseInt } from "client/Misc/parseUtils";
import { HubLayoutContext } from "../HubLayout";
import {
	HubDataSource,
	HubItemCategoryModel,
	HubItemHubSidebarNodeModel,
	HubItemRootHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useUnavailableCategoryRedirect } from "../useUnavailableCategoryRedirect";
import { useCategories } from "../useCategories";
import { useCanOpenHubManagement } from "../useCanOpenHubManagement";
import { useForceRerender } from "client/Hooks";
import {
	HubDialogContext,
	HubDialogContextData,
	ViewSpecificHandlers
} from "../common";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { never } from "client_legacy/ndaw/errors";
import { HubSidebarValue } from "../Sidebar/HubSidebar";
import { HubEvent, publish } from "../events";
import { hubRouting } from "../routing";

export type KnowledgeItemsPageProps = {
	id?: number;
	categoryId?: number | "all";
	groupId?: number;
	commentId?: number;
	onNavigate: NavigationCallback;
};

export const KnowledgeItemsPage = (props: KnowledgeItemsPageProps): ReactElement | null => {
	const {
		id,
		categoryId,
		groupId,
		commentId,
		onNavigate
	} = props;

	const {
		hierarchy,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities,
		activeFilter,
		changeActiveFilter,
		changeActiveSidebarItem,
		addOnBeforeNavigateToSidebarItemHandler
	} = useContext(HubLayoutContext);

	const [itemCategoryId, setItemCategoryId] = useState<OptionalModel<number | null>>({ isSet: false });
	const [forceRerender] = useForceRerender();
	const viewSpecificHandlersRef: RefObject<ViewSpecificHandlers | undefined> = useRef<ViewSpecificHandlers>(undefined);

	const categories: OptionalModel<readonly HubItemCategoryModel[]> = useCategories(
		hierarchy,
		HubItemKind.KnowledgeItem
	);

	const knowledgeItemsHierarchy = useMemo(
		(): OptionalModel<readonly HubItemHubSidebarNodeModel[]> => {
			if (hierarchy.length === 0) {
				return { isSet: false };
			}

			const rootItem: HubItemRootHubSidebarNodeModel | undefined = hierarchy.find(
				(x): x is HubItemRootHubSidebarNodeModel => x.source === HubDataSource.KnowledgeItem
			);

			if (!rootItem) {
				console.error("Knowledge items data was not found");
				return { isSet: false };
			}

			return {
				isSet: true,
				value: rootItem.children
			};
		},
		[hierarchy]
	);

	const canOpenHubManagement: boolean = useCanOpenHubManagement(
		categories,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities
	);

	useUnavailableCategoryRedirect(
		HubItemKind.KnowledgeItem,
		categories,
		categoryId !== "all"
			? categoryId
			: undefined,
		onNavigate
	);

	const computedSidebarValue = useMemo(
		(): HubSidebarValue =>itemCategoryId.isSet
			? {
				source: HubDataSource.KnowledgeItem,
				viewIdentifier: {
					kind: "categoryId",
					categoryId: itemCategoryId.value ?? undefined
				}
			}
			: isNotNil(groupId)
				? {
					source: HubDataSource.KnowledgeItem,
					viewIdentifier: {
						kind: "groupId",
						groupId
					}
				}
				: {
					source: HubDataSource.KnowledgeItem,
					viewIdentifier: {
						kind: "categoryId",
						categoryId
					}
				},
		[
			categoryId,
			groupId,
			itemCategoryId
		]
	);

	useEffect(
		(): void => {
			changeActiveSidebarItem(computedSidebarValue);
		},
		[
			computedSidebarValue,
			changeActiveSidebarItem
		]
	);

	const tryLeaveThePageHandler = useCallback(
		async (): Promise<TryResult<void>> => {
			if (viewSpecificHandlersRef.current?.beforeSwitchingCategoryHandler) {
				const shouldStay: boolean = await viewSpecificHandlersRef.current
					.beforeSwitchingCategoryHandler();

				if (shouldStay) {
					return { wasSuccessful: false };
				}
			}

			return {
				wasSuccessful: true,
				result: undefined
			};
		},
		[]
	);

	const navigateHandler = useCallback(
		async (options: KnowledgeItemRouterUrlOptions): Promise<void> => {
			if (!(await tryLeaveThePageHandler()).wasSuccessful) {
				return;
			}

			const navigation: Navigation = getHubNavigationForKnowledgeItem(options);
			onNavigate(navigation);
		},
		[onNavigate, tryLeaveThePageHandler]
	);

	const changeViewHandler = useCallback(
		(view: KnowledgeItemsView): void => {
			let options: KnowledgeItemRouterUrlOptions;

			switch (view.kind) {
				case "groupOverview":
					options = {
						source: HubDataSource.KnowledgeItem,
						viewIdentifier: isNotNil(view.groupId)
							? {
								kind: "groupId",
								groupId: view.groupId
							}
							: undefined
					};
					break;
				case "categoryOverView":
					options = {
						source: HubDataSource.KnowledgeItem,
						viewIdentifier: {
							kind: "categoryId",
							categoryId
						}
					};
					break;
				case "details":
					options = {
						source: HubDataSource.KnowledgeItem,
						itemId: view.id,
						viewIdentifier: {
							kind: "categoryId",
							categoryId
						}
					};
					break;
				default:
					never(view);
			}

			navigateHandler(options);
		},
		[categoryId, navigateHandler]
	);

	const selectCategoryHandler = useCallback(
		(categoryId: number | "all" | null): void => {
			navigateHandler({
				source: HubDataSource.KnowledgeItem,
				viewIdentifier: {
					kind: "categoryId",
					categoryId: categoryId ?? undefined
				}
			});
		},
		[navigateHandler]
	);

	const hubDialogContext = useMemo(
		(): HubDialogContextData => {
			return {
				setViewSpecificHandlers: (options): void => {
					viewSpecificHandlersRef.current = options;
					forceRerender();
				}
			};
		},
		[forceRerender]
	);

	const hierarchyChangedHandler = useCallback(
		(): void => {
			publish(HubEvent.HierarchyChanged, undefined);
		},
		[]
	);

	useEffect(
		(): (() => void) | void => {
			return addOnBeforeNavigateToSidebarItemHandler(
				async (): Promise<boolean> => {
					const { current: viewSpecificHandlers } = viewSpecificHandlersRef;

					if (!viewSpecificHandlers?.beforeSwitchingCategoryHandler) {
						return false;
					}

					return await viewSpecificHandlers.beforeSwitchingCategoryHandler();
				}
			);
		},
		[addOnBeforeNavigateToSidebarItemHandler]
	);

	const backHandler: (() => void) | undefined = viewSpecificHandlersRef.current
		?.backHandler;

	const view = useMemo(
		(): KnowledgeItemsView => {
			if (isNotNil(id)) {
				return {
					kind: "details",
					id,
					commentId
				};
			}

			if (isNotNil(categoryId)) {
				return {
					kind: "categoryOverView"
				};
			}

			return {
				kind: "groupOverview",
				groupId: groupId ?? null
			};
		},
		[
			categoryId,
			commentId,
			groupId,
			id
		]
	);

	if (!categories.isSet || !knowledgeItemsHierarchy.isSet) {
		return null;
	}

	const selectedCategoryId: number | null = categoryId === "all" || categoryId === undefined
		? null
		: categoryId;

	return (
		<>
			{backHandler && (
				<TitleBarBackButton
					onBack={backHandler}
				/>
			)}

			<HubDialogContext.Provider value={hubDialogContext}>
				<KnowledgeItemsPageContent
					view={view}
					selectedCategoryId={selectedCategoryId}
					hierarchy={knowledgeItemsHierarchy.value}
					onViewChange={changeViewHandler}
					onSelectCategory={selectCategoryHandler}
					categories={categories.value}
					onHierarchyChanged={hierarchyChangedHandler}
					hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
					activeFilter={activeFilter}
					onActiveFilterChange={changeActiveFilter}
					onItemCategoryChange={setItemCategoryId}
					canOpenHubManagement={canOpenHubManagement}
				/>
			</HubDialogContext.Provider>
		</>
	);
};
KnowledgeItemsPage.displayName = "KnowledgeItemsPage";

function tryParseCategory(value: string | nil): TryResult<number | "all"> {
	if (isNil(value)
		|| value === ""
	) {
		return { wasSuccessful: false };
	}

	if (value.toUpperCase() === "ALL" ) {
		return {
			wasSuccessful: true,
			result: "all"
		};
	}

	return tryParseInt(value);
}

export const KnowledgeItemsPageForRouter: FunctionComponent = connectToRouter(
	KnowledgeItemsPage,
	{
		"id": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"categoryId": {
			isRequired: false,
			tryParse: tryParseCategory,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"groupId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"commentId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type KnowledgeItemsPageRouteParams = RouteParametersFromProps<
	KnowledgeItemsPageProps,
	never,
	"id"
>;

export type KnowledgeItemsPageQueryParams = QueryParametersFromProps<
	KnowledgeItemsPageProps,
	"categoryId"
	| "groupId"
	| "commentId"
>;

export type KnowledgeItemViewIdentifier = {
	kind: "categoryId";
	categoryId?: number | "all";
} | {
	kind: "groupId";
	groupId: number;
};

export type KnowledgeItemRouterUrlOptions = {
	source: HubDataSource.KnowledgeItem;
	viewIdentifier?: KnowledgeItemViewIdentifier;
} & ({
	itemId?: undefined;
} | {
	itemId: number;
	commentId?: number;
});

export function getHubNavigationForKnowledgeItem(
	options: KnowledgeItemRouterUrlOptions
): Navigation {
	const { viewIdentifier, itemId } = options;

	return hubRouting.knowledgeItems({
		routeParams: {
			id: isNotNil(itemId)
				? `${itemId}`
				: undefined
		},
		queryParams: {
			...(viewIdentifier
				? viewIdentifier.kind === "categoryId"
					? {
						categoryId: isNotNil(viewIdentifier.categoryId)
							? `${viewIdentifier.categoryId}`
							: undefined
					}
					: {
						groupId: `${viewIdentifier.groupId}`
					}
				: undefined
			),
			commentId: typeof itemId === "number"
				&& isNotNil(options.commentId)
					? `${options.commentId}`
					: undefined
		}
	});
}

export function getHubUrlForKnowledgeItem(
	options: KnowledgeItemRouterUrlOptions
): string {
	return createBrowserNavigationUrl(
		getHubNavigationForKnowledgeItem(options)
	);
}
